import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./menu.scss"
import { menuData } from "./menuData";
import { useAuth } from "../../contexts/AuthContext";

const Menu = React.memo(() => {
  console.log('🔄 Menu component rendering');
  const { t } = useTranslation();
  const { hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();

  // Function to check if user has permission for a specific menu item
  const hasMenuPermission = (url: string) => {
    switch (url) {
      case "/":
        return true; // Home page is always accessible
      case "/order/index":
        return hasPermission("View Order");
      case "/shipforme":
        return hasPermission("View Ship For Me");
      case "/buyforme-groups":
        return hasPermission("List Order Group") && hasPermission("View Order");
      case "/shipforme-groups":
        return hasPermission("List Order Group") && hasPermission("View Ship For Me");
      case "/customers":
        return hasPermission("View Customer");
      case "/users":
        return hasPermission("View User");
      case "/roles":
        return hasPermission("View Role");
      case "/invoices":
        return hasPermission("View Invoice");
      case "/config":
        return hasAnyPermission("View Config", "Update Config");
      case "/payment-methods":
        return hasPermission("View Payment Method");
      case "/payment-gateways":
        return hasPermission("View Payment Gateway");
      case "/shipping-companies":
        return hasPermission("View Shipping Company");
      case "/currencies":
        return hasPermission("View Currency");
      case "/vouchers":
        return hasPermission("View Voucher");
      case "/categories":
        return hasPermission("View Category");
      case "/countries":
        return hasPermission("View Country");
      case "/cities":
        return hasPermission("View City");
      case "/measurement-units":
        return hasPermission("View Measurement Unit");
      case "/faqs":
        return hasPermission("View FAQ");
      case "/faq-categories":
        return hasPermission("View FAQ Category");
      case "/recovery":
        return hasPermission("View Recovery");
      case "/settings/recaptcha":
        return hasAnyPermission("View Recaptcha Settings", "Update Recaptcha Settings");
      case "/cms/sliders":
        return hasPermission("View Sliders");
      case "/cms/general-info":
        return hasAnyPermission("View General Info", "Update General Info");
      default:
        return true; // Default to show if no specific permission defined
    }
  };

  // Function to check if user has permission for at least one item in a section
  const hasSectionPermission = (section: typeof menuData[0]) => {
    return section.listItems.some((listItem) => hasMenuPermission(listItem.url));
  };

  return (
    <div className="menu">
      {menuData.map((item) => {
        // Only render the section if user has permission for at least one item
        if (!hasSectionPermission(item)) {
          return null;
        }

        return (
          <div className="item" key={item.id}>
            <span className="title">{t(item.title)}</span>
            {item.listItems.map((listItem) => {
              const IconComponent = listItem.icon;
              const isActive = location.pathname === listItem.url;

              return hasMenuPermission(listItem.url) && (
                <Link
                  className={`listItem ${isActive ? 'active' : ''}`}
                  to={listItem.url}
                  key={listItem.id}
                >
                  <div className="iconWrapper">
                    <IconComponent className="menuIcon" />
                  </div>
                  <span className="listItemTitle">{t(listItem.title)}</span>
                </Link>
              )
            })}
          </div>
        );
      })}
    </div>
  );
});

Menu.displayName = 'Menu';
export default Menu;
